import {
  Cell,
  CellTemplate,
  Compatible,
  getCellProperty,
  isAlphaNumericKey,
  keyCodes,
  OptionType,
  Uncertain,
  UncertainCompatible,
} from '@silevis/reactgrid'
import i18n from 'i18next'
import * as React from 'react'
import SelectButton from 'ui/components/SelectButton/SelectButton'

export interface CopyFromCell extends Cell {
  type: 'copyFrom'
  columnId: string
  values: OptionType[]
  selectedValue: string
}

function isCopyFromCell(cell: UncertainCompatible<Cell>): cell is UncertainCompatible<CopyFromCell> {
  return cell.type === 'copyFrom'
}

export class CopyFromCellTemplate implements CellTemplate<CopyFromCell> {
  getCompatibleCell(uncertainCell: Uncertain<CopyFromCell>): Compatible<CopyFromCell> {
    const columnId = getCellProperty(uncertainCell, 'columnId', 'string')
    const values = getCellProperty(uncertainCell, 'values', 'object') as OptionType[]
    const selectedValue = getCellProperty(uncertainCell, 'selectedValue', 'string')
    return { ...uncertainCell, columnId, values, selectedValue, value: 0, text: selectedValue, type: 'copyFrom' }
  }

  isFocusable(): boolean {
    return false
  }

  getClassName(cell: Compatible<CopyFromCell>): string {
    return cell.className ? cell.className : ''
  }

  handleKeyDown(
    cell: Compatible<CopyFromCell>,
    keyCode: number,
    ctrl: boolean,
    _shift: boolean,
    alt: boolean
  ): { cell: Compatible<CopyFromCell>; enableEditMode: boolean } {
    if (!ctrl && !alt && isAlphaNumericKey(keyCode)) return { cell, enableEditMode: true }
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
    }
  }

  update(cell: Compatible<CopyFromCell>, cellToMerge: UncertainCompatible<Cell>): Compatible<CopyFromCell> {
    if (!isCopyFromCell(cellToMerge)) {
      return cell
    }

    return this.getCompatibleCell({ ...cell, columnId: cellToMerge.columnId, selectedValue: cellToMerge.selectedValue })
  }

  render(
    cell: Compatible<CopyFromCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CopyFromCell>, commit: boolean) => void
  ): React.ReactNode {
    if (!isInEditMode) {
      return (
        <>
          {cell.columnId && (
            <SelectButton
              icon={'fal fa-copy'}
              link
              noPadding
              menuItems={cell.values.map((dataset) => ({
                label: dataset.label,
                onClick: () => {
                  const updatedCell = this.getCompatibleCell({
                    ...cell,
                    selectedValue: dataset.value,
                  })

                  onCellChanged(updatedCell, true)
                },
              }))}
            >
              {i18n.t('Copy from')}
            </SelectButton>
          )}
        </>
      )
    }
  }
}
